// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Haggle — every colour that carries meaning, in one place so it can be measured.
//
// FIVE COLOURS CAN NEVER BE MUTUALLY 3:1. n mutually-3:1 colours need a luminance span of 3^(n-1),
// and the largest span sRGB can offer is 21 — so THREE is the hard ceiling and this game has five
// wares. That is arithmetic, not an excuse, and contrast.test.ts asserts the arithmetic rather than
// pretending the gate was met. Ware identity is therefore carried by a GLYPH and the printed NAME on
// every single chip; colour is redundant decoration everywhere it appears. What IS gated: every
// meaningful colour clears 3:1 against every surface it can sit on, and the ink printed on a ware
// chip clears 4.5:1 on that chip.

export const PALETTE = {
  ground: '#12161C',
  card: '#1E242E',
  tariff: '#262E3A',
  panel: '#1A202A',
  ink: '#0B0E12',
  text: '#E8EDF4',
  dim: '#A6B2C4',
  edge: '#6B7A90',
  copper: '#E8863C',
  glass: '#4FB6E8',
  resin: '#C879E8',
  tin: '#58C98A',
  salt: '#C9D2DE',
  last: '#FFC857',
  dry: '#FF6B5E',
  chit: '#FFC857',
  // The two seat colours are separated in LUMINANCE (2.23:1), not only in hue. An earlier pair —
  // a bright teal and a soft rose — measured 1.11:1 against each other: distinguishable to most
  // eyes, identical in greyscale and nearly identical to a player with a red-green deficiency, on
  // the one element that says who is winning. The HUD also labels them in words.
  you: '#9FE8D6',
  them: '#D9718F',
} as const;

export type PaletteKey = keyof typeof PALETTE;

/** The surfaces a meaningful colour can be painted on. Everything is measured against all of them. */
export const SURFACES: PaletteKey[] = ['ground', 'card', 'tariff', 'panel'];

/** Colours that carry meaning on the play surface and must clear 3:1 on every surface above. */
export const MEANINGFUL: PaletteKey[] = [
  'copper',
  'glass',
  'resin',
  'tin',
  'salt',
  'last',
  'dry',
  'chit',
  'text',
  'you',
  'them',
  'edge',
];

export function srgbToLinear(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const r = srgbToLinear(parseInt(h.slice(0, 2), 16));
  const g = srgbToLinear(parseInt(h.slice(2, 4), 16));
  const b = srgbToLinear(parseInt(h.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The glyph is the identity. A greyscale screenshot of the board still reads correctly, and so does
 * a board seen by a player with any of the three dichromacies.
 */
export const WARE_GLYPH: Record<string, string> = {
  copper: '◎',
  glass: '▽',
  resin: '◆',
  tin: '▬',
  salt: '✦',
  porter: '♟',
};
